// src/components/AssetNewsPanel.js - News Panel Component
import React from 'react';

export const AssetNewsPanel = ({ news, symbol, theme, status, onRetry }) => {
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      borderRadius: '16px',
      padding: '1.5rem',
      position: 'sticky',
      top: '2rem',
      maxHeight: 'calc(100vh - 4rem)',
      overflowY: 'auto',
      backdropFilter: 'blur(10px)'
    }}>
      
      {/* Header */}
      <div style={{ 
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h3 style={{ 
          color: '#D4AF37',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '1.1rem'
        }}>
          📰 Notícias - {symbol}
          {status === 'fetching' && (
            <span style={{
              fontSize: '0.7rem',
              background: 'rgba(212, 175, 55, 0.2)',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              color: '#D4AF37',
              animation: 'pulse 1.5s infinite'
            }}>
              Buscando...
            </span>
          )}
        </h3>
      </div>

      {/* === LOADING STATE === */}
      {status === 'loading' && (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 1rem',
          color: theme.textSecondary
        }}>
          <div style={{ 
            fontSize: '2.5rem', 
            marginBottom: '1rem',
            animation: 'spin 2s linear infinite'
          }}>
            ⏳
          </div>
          <div style={{ fontSize: '1rem', fontWeight: '600' }}>
            Carregando notícias...
          </div>
        </div>
      )}

      {/* === FETCHING STATE (202 Response) === */}
      {status === 'fetching' && (
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(212, 175, 55, 0.05))',
          border: `1px solid rgba(212, 175, 55, 0.3)`,
          borderRadius: '12px',
          padding: '2.5rem 1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '3rem', 
            marginBottom: '1rem',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            🔍
          </div>
          <div style={{ 
            fontSize: '1rem', 
            fontWeight: '700', 
            color: theme.textPrimary,
            marginBottom: '0.5rem'
          }}>
            Buscando notícias recentes
          </div>
          <div style={{ 
            fontSize: '0.85rem', 
            color: theme.textSecondary,
            lineHeight: '1.5'
          }}>
            Isso pode levar alguns segundos...
            <br />
            <span style={{ opacity: 0.7 }}>Aguarde ou atualize a página</span>
          </div>
        </div>
      )}

      {/* === ERROR STATE === */}
      {status === 'error' && (
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.1)',
          border: `1px solid rgba(239, 68, 68, 0.3)`,
          borderRadius: '12px',
          padding: '2.5rem 1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <div style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#ef4444',
            marginBottom: '1rem'
          }}>
            Erro ao carregar notícias
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: `1px solid rgba(239, 68, 68, 0.4)`,
                borderRadius: '8px',
                padding: '0.5rem 1.5rem',
                color: '#ef4444',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              }}
            >
              🔄 Tentar Novamente
            </button>
          )}
        </div>
      )}

      {/* === NO NEWS STATE === */}
      {status === 'ready' && news.length === 0 && (
        <div style={{ 
          background: 'rgba(0, 0, 0, 0.05)',
          borderRadius: '12px',
          padding: '3rem 1.5rem',
          textAlign: 'center',
          color: theme.textSecondary
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            Nenhuma notícia recente
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
            Novas notícias aparecerão aqui
          </div>
        </div>
      )}

      {/* === NEWS LIST === */}
      {status === 'ready' && news.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {news.map((item, i) => (
            <NewsItem 
              key={i} 
              item={item} 
              theme={theme}
            />
          ))}
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

// ============================================
// NEWS ITEM COMPONENT
// ============================================

const NewsItem = ({ item, theme }) => {
  const getSentimentColor = (sentiment) => {
    switch(sentiment) {
      case 'positive': return '#4ade80';
      case 'negative': return '#ef4444';
      default: return theme.textSecondary;
    }
  };

  const getSentimentEmoji = (sentiment) => {
    switch(sentiment) {
      case 'positive': return '📈';
      case 'negative': return '📉';
      default: return '📰';
    }
  };

  const getSentimentLabel = (sentiment) => {
    switch(sentiment) {
      case 'positive': return 'POSITIVA';
      case 'negative': return 'NEGATIVA';
      default: return 'NEUTRA';
    }
  };

  return (
    <div
      style={{
        padding: '1rem',
        background: 'rgba(0, 0, 0, 0.05)',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `2px solid transparent`
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(212, 175, 55, 0.15)';
        e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)';
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
      onClick={() => window.open(item.url, '_blank')}
    >
      {/* Sentiment + Relevance Badges */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
        {item.sentiment && (
          <span style={{
            fontSize: '0.7rem',
            background: `${getSentimentColor(item.sentiment)}20`,
            color: getSentimentColor(item.sentiment),
            padding: '0.25rem 0.6rem',
            borderRadius: '6px',
            fontWeight: 'bold',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            {getSentimentEmoji(item.sentiment)} {getSentimentLabel(item.sentiment)}
          </span>
        )}
        
        {item.relevance_score >= 1.0 && (
          <span style={{
            fontSize: '0.7rem',
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#D4AF37',
            padding: '0.25rem 0.6rem',
            borderRadius: '6px',
            fontWeight: 'bold'
          }}>
            🎯 RELEVANTE
          </span>
        )}
      </div>

      {/* Title */}
      <div style={{
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: theme.textPrimary,
        marginBottom: '0.5rem',
        lineHeight: '1.4'
      }}>
        {item.title}
      </div>

      {/* Description */}
      {item.description && (
        <div style={{
          fontSize: '0.8rem',
          color: theme.textSecondary,
          marginBottom: '0.75rem',
          lineHeight: '1.4',
          opacity: 0.85
        }}>
          {item.description.substring(0, 150)}
          {item.description.length > 150 && '...'}
        </div>
      )}

      {/* Meta Info */}
      <div style={{
        fontSize: '0.7rem',
        color: theme.textSecondary,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        opacity: 0.7
      }}>
        <span>{item.source || 'Google News'}</span>
        <span>•</span>
        <span>{formatTime(item.published_at)}</span>
      </div>
    </div>
  );
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatTime = (timestamp) => {
  if (!timestamp) return 'Agora';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `${diffDays}d atrás`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  } catch (e) {
    return 'Recente';
  }
};

export default AssetNewsPanel;
